import React, { useEffect, useState } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Divider,
    CircularProgress,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Chip,
    Stack,
    Paper,
    Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonIcon from '@mui/icons-material/Person';
import { Customer, KycData, BankData } from 'src/api/types/customer.types';
import { customersApi } from 'src/api/endpoints/customers';
import { format, parseISO } from 'date-fns';

interface CustomerDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    userId: string | null;
}

const CustomerDetailsDrawer: React.FC<CustomerDetailsDrawerProps> = ({
    open,
    onClose,
    userId
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [user, setUser] = useState<Customer | null>(null);
    const [kycData, setKycData] = useState<KycData[]>([]);
    const [bankData, setBankData] = useState<BankData[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && userId) {
            fetchUserData(userId);
        } else {
            // Reset state when drawer closes
            setUser(null);
            setKycData([]);
            setBankData([]);
            setError(null);
        }
    }, [open, userId]);

    const fetchUserData = async (id: string) => {
        try {
            setLoading(true);
            setError(null);

            // Fetch KYC details
            const kycResponse = await customersApi.getKycDetails(id);

            if (kycResponse.status && kycResponse.data.response.status) {
                setUser(kycResponse.data.response.data.user[0]);
                setKycData(kycResponse.data.response.data.kycData);
            } else {
                setError('Failed to fetch KYC details');
            }

            // Fetch bank details
            try {
                const bankResponse = await customersApi.getBankDetails(id);
                if (bankResponse.status) {
                    setBankData(bankResponse.data);
                }
            } catch (error) {
                console.error('Error fetching bank details:', error);
                // Don't set error here to keep KYC data visible if bank data fails
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setError('Error fetching user data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        try {
            return format(parseISO(dateString), 'dd/MM/yyyy HH:mm');
        } catch (e) {
            return dateString;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'approved':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 450 },
                    p: 3,
                    bgcolor: 'background.default'
                }
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5"
sx={{ fontWeight: 600 }}>Customer Details</Typography>
                <IconButton onClick={onClose}
edge="end"
color="primary">
                    <CloseIcon />
                </IconButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Paper sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 2 }}>
                    <Typography>{error}</Typography>
                </Paper>
            ) : user ? (
                <Stack spacing={3}>
                    {/* User Information Card */}
                    <Card elevation={2}>
                        <CardHeader
                            title="User Information"
                            avatar={
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    <PersonIcon />
                                </Avatar>
                            }
                        />
                        <CardContent>
                            <Grid container
spacing={2}>
                                <Grid item
xs={12}>
                                    <Typography variant="caption"
color="text.secondary">User ID</Typography>
                                    <Typography variant="body2"
gutterBottom
sx={{ wordBreak: 'break-all' }}>
                                        {user.user_id}
                                    </Typography>
                                </Grid>
                                <Grid item
xs={6}>
                                    <Typography variant="caption"
color="text.secondary">Email</Typography>
                                    <Typography variant="body2"
gutterBottom>
                                        {user.email}
                                    </Typography>
                                </Grid>
                                <Grid item
xs={6}>
                                    <Typography variant="caption"
color="text.secondary">Mobile</Typography>
                                    <Typography variant="body2"
gutterBottom>
                                        {user.mobile}
                                    </Typography>
                                </Grid>
                                <Grid item
xs={6}>
                                    <Typography variant="caption"
color="text.secondary">User Type</Typography>
                                    <Typography variant="body2"
gutterBottom>
                                        {user.user_type}
                                    </Typography>
                                </Grid>
                                <Grid item
xs={6}>
                                    <Typography variant="caption"
color="text.secondary">Created At</Typography>
                                    <Typography variant="body2"
gutterBottom>
                                        {formatDate(user.created_at)}
                                    </Typography>
                                </Grid>
                                <Grid item
xs={12}>
                                    <Typography variant="caption"
color="text.secondary">Client ID</Typography>
                                    <Typography variant="body2"
sx={{ wordBreak: 'break-all' }}>
                                        {user.client_id}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* KYC Information Cards */}
                    {kycData.length > 0 ? (
                        kycData.map((kyc, index) => (
                            <Card key={kyc.kyc_id} elevation={2}>
                                <CardHeader
                                    title={`${kyc.document_type.toUpperCase()} Information`}
                                    subheader={`Document ${index + 1}`}
                                    avatar={
                                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                            <VerifiedUserIcon />
                                        </Avatar>
                                    }
                                    action={
                                        <Chip
                                            label={kyc.kyc_status.toUpperCase()}
                                            color={getStatusColor(kyc.kyc_status) as any}
                                            size="small"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    }
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Document Type</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {kyc.document_type.toUpperCase()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Document Name</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {kyc.document_name.toUpperCase()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary">Document Number</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {kyc.document_number}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Full Name</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {kyc.full_name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {formatDate(kyc.dob)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Country</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {kyc.country}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Verified By</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {kyc.verified_by?.toUpperCase() || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Created At</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {formatDate(kyc.created_at)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Updated At</Typography>
                                            <Typography variant="body2" gutterBottom>
                                                {formatDate(kyc.updated_at)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Card elevation={2}>
                            <CardHeader
                                title="KYC Information"
                                avatar={
                                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                        <VerifiedUserIcon />
                                    </Avatar>
                                }
                            />
                            <CardContent>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No KYC data available
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Bank Information Cards */}
                    {bankData.length > 0 ? (
                        bankData.map((bank ) => (
                            <Card key={bank.bank_id}
elevation={2}>
                                <CardHeader
                                    title="Bank Information"
                                    subheader={bank.bank_name || 'Bank Account'}
                                    avatar={
                                        <Avatar sx={{ bgcolor: 'info.main' }}>
                                            <AccountBalanceIcon />
                                        </Avatar>
                                    }
                                    action={
                                        <Chip
                                            label={bank.bank_status.toUpperCase()}
                                            color={getStatusColor(bank.bank_status) as any}
                                            size="small"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    }
                                />
                                <CardContent>
                                    <Grid container
spacing={2}>
                                        <Grid item
xs={12}>
                                            <Typography variant="caption"
color="text.secondary">Account Number</Typography>
                                            <Typography variant="body2"
gutterBottom>
                                                {bank.account_number}
                                            </Typography>
                                        </Grid>
                                        <Grid item
xs={6}>
                                            <Typography variant="caption"
color="text.secondary">IFSC Code</Typography>
                                            <Typography variant="body2"
gutterBottom>
                                                {bank.ifsc_code}
                                            </Typography>
                                        </Grid>
                                        <Grid item
xs={6}>
                                            <Typography variant="caption"
color="text.secondary">Created At</Typography>
                                            <Typography variant="body2"
gutterBottom>
                                                {formatDate(bank.created_at)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Card elevation={2}>
                            <CardHeader
                                title="Bank Information"
                                avatar={
                                    <Avatar sx={{ bgcolor: 'info.main' }}>
                                        <AccountBalanceIcon />
                                    </Avatar>
                                }
                            />
                            <CardContent>
                                <Typography variant="body2"
color="text.secondary"
align="center">
                                    No bank data available
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            ) : (
                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                    <Typography variant="body2"
color="text.secondary">
                        No user data available
                    </Typography>
                </Paper>
            )}
        </Drawer>
    );
};

export default CustomerDetailsDrawer; 